import { DateTime } from '@mode-switch/express';
import { DataType, EncFilterField, Encoding, FilterType, Guide, SelectionMode, VariableType } from '@mode-vegas/vegas';
import { FlamingoTypes, ReportTypes } from '@mode/shared/contract-common';
import { isValid } from 'date-fns';
import { isPresent } from '@mode/shared/util-js';

export function isDiscrete(filter: ReportTypes.FilterDefinition): boolean {
  return filter.varType === VariableType.DISCRETE;
}

export function isContinuous(filter: ReportTypes.FilterDefinition): boolean {
  return filter.varType === VariableType.CONTINUOUS;
}

export function isDateType(filter: ReportTypes.FilterDefinition): boolean {
  switch (filter.formulaType) {
    case DataType.DATE:
    case DataType.DATETIME:
      return true;
    default:
      return false;
  }
}

export function isDiscreteDateType(filter: ReportTypes.FilterDefinition): boolean {
  switch (filter.formulaType) {
    case DataType.DATEQTR:
    case DataType.DATEMONTH:
    case DataType.DATEWKDAY:
      return true;
    default:
      return false;
  }
}

export function isStringType(filter: ReportTypes.FilterDefinition): boolean {
  switch (filter.formulaType) {
    case DataType.STRING:
      return true;
    default:
      return false;
  }
}

export function isNumericType(filter: ReportTypes.FilterDefinition): boolean {
  switch (filter.formulaType) {
    case DataType.FLOAT:
    case DataType.INTEGER:
    case DataType.NUMBER:
      return true;
    default:
      return false;
  }
}

export function convertSelectedValuesToDates(value: ReportTypes.ReportFilterRange) {
  const { min, max } = value;

  const minDate = min == null ? null : new Date(min);
  const maxDate = max == null ? null : new Date(max);

  if (minDate != null && isValid(minDate)) {
    minDate.setUTCHours(0, 0, 0, 0); // set to start of day
  }

  if (maxDate != null && isValid(maxDate)) {
    maxDate.setUTCHours(23, 59, 59, 999); // set last second of day
  }

  /**
   * Force casting here because vegas EncFilterField has a mistake on min/max
   * where it doesn't accept null values.
   **/

  return {
    min: (minDate && minDate.valueOf()) as number,
    max: (maxDate && maxDate.valueOf()) as number,
  };
}

export function getControlForFilterType(
  type: ReportTypes.ReportFilterType,
  controlType = ReportTypes.ReportFilterControlType.List
) {
  switch (type) {
    case ReportTypes.ReportFilterType.Range:
      return ReportTypes.ReportFilterControlType.Range;
    case ReportTypes.ReportFilterType.Multi:
      if (controlType === ReportTypes.ReportFilterControlType.Dropdown) {
        return ReportTypes.ReportFilterControlType.Dropdown;
      }
      return ReportTypes.ReportFilterControlType.List;
    case ReportTypes.ReportFilterType.Single:
      if (controlType === ReportTypes.ReportFilterControlType.Dropdown) {
        return ReportTypes.ReportFilterControlType.Dropdown;
      }
      return ReportTypes.ReportFilterControlType.List;
  }
}

export function getVarTypeForFilterType(type: ReportTypes.ReportFilterType) {
  return type === ReportTypes.ReportFilterType.Range ? VariableType.CONTINUOUS : VariableType.DISCRETE;
}

const guide = new Guide();
// Warning, will not overwrite the same filters, since there's no easy way to figure that out.
// So don't call this more than once on the same encoding.
export function addReportFiltersToChartEncoding(
  view: FlamingoTypes.ModeView | undefined,
  encoding: Encoding,
  filters: ReadonlyArray<ReportTypes.ReportFilterControl>,
  merge = false
) {
  if (filters == null || filters.length <= 0) {
    return encoding;
  }

  const encoder = guide.newBatchOperation(encoding);
  const encFilters = filters
    .map<EncFilterField | undefined>((filter) => {
      switch (filter.filterType) {
        case ReportTypes.ReportFilterType.Range:
          if (isDateType(filter)) {
            const selectedAsDates = convertSelectedValuesToDates(filter.selected);
            return {
              formula: filter.formula,
              variable: { type: VariableType.CONTINUOUS },
              filterType: FilterType.ContinuousDate,
              range: { max: selectedAsDates.max, min: selectedAsDates.min },
              includeMin: true,
              includeMax: false,
              includeNulls: false, // Not used in the UI currently
            };
          } else {
            return {
              formula: filter.formula,
              variable: { type: VariableType.CONTINUOUS },
              filterType: FilterType.Continuous,
              range: { max: filter.selected.max as number, min: filter.selected.min as number },
              includeMin: true,
              includeMax: true,
              includeNulls: false, // Not used in the UI currently
            };
          }
        case ReportTypes.ReportFilterType.Multi:
          if (!filter.selected.all) {
            return {
              formula: filter.formula,
              variable: { type: VariableType.DISCRETE },
              filterType: FilterType.Discrete,
              selection: {
                selected: filter.selected.values ? Array.from(filter.selected.values) : [],
                mode: SelectionMode.INCLUDE,
              },
            };
          }
          break;
        case ReportTypes.ReportFilterType.Single:
          if (!filter.selected.all) {
            if (filter.dataType === DataType.BOOLEAN) {
              return {
                formula: filter.formula,
                variable: { type: VariableType.DISCRETE },
                filterType: FilterType.DiscreteBoolean,
                selection: {
                  selected: [filter.selected.value as ReportTypes.ReportFilterValue],
                  mode: SelectionMode.INCLUDE,
                },
              };
            } else {
              return {
                formula: filter.formula,
                variable: { type: VariableType.DISCRETE },
                filterType: FilterType.Discrete,
                selection: {
                  selected: [filter.selected.value as ReportTypes.ReportFilterValue],
                  mode: SelectionMode.INCLUDE,
                },
              };
            }
          }
          break;
      }
    })
    .filter(isPresent);

  if (view != null && merge) {
    for (const filter of encoding.filter) {
      /**
       * Ensure relative date filters have the correct w/y start values before attempting
       * to merge in report filters. When combining date filters, Vegas converts relative
       * date filters to continuous date filters such that the resulting filter is a simple
       * intersection of the two ranges; we provide the workspace w/y config for relative
       * date filters that don't have one or both defined
       */
      if (filter.filterType === FilterType.RelativeDate) {
        const yearStart = DateTime.yearStartMonth(filter.yearStart ?? view.yearStart);
        const weekStart = DateTime.weekStartDow(filter.weekStart ?? view.weekStart);
        // "addEncFilter" is an upsert
        encoder.addEncFilter({
          ...filter,
          yearStart,
          weekStart,
        });
      }
    }
  }

  encFilters.forEach((f) => {
    if (merge) {
      encoder.mergeEncFilter(f);
    } else {
      encoder.appendEncFilter(f);
    }
  });

  return encoder.complete().encoding;
}
